import { UseThreeJsOnSuccess } from '@/hooks/useThreeJs/useThreeJs';
import React, { memo } from 'react';
import ThreeDModelUploadModal from './ThreeDModelUploadModal';
import ThreeDModelViewer from './ThreeDModelViewer';
import useThreeDModel from './useThreeDModel';
import { CurrentValues } from './utils';

const MemoizedThreeDModelViewer = memo(ThreeDModelViewer);

interface SlideObject3DModelProps {
  isSaving: boolean;
  interactable: boolean;
  modalManager: ModalManager;
  onSuccess: UseThreeJsOnSuccess;
  slideObject: {
    id: string;
    threeDModels: {
      id: string;
      fileName: string;
      modelUrl: string;
      textures: {
        id: string;
        imageUrl: string;
        imageModels: {
          id: string;
          textureType: string;
        }[];
      }[];
    }[];
  };
}

const SlideObject3DModel = ({
  interactable,
  modalManager,
  onSuccess,
  slideObject,
}: SlideObject3DModelProps) => {
  const { data: threeDModel } = useThreeDModel({ slideObjectId: slideObject.id });

  if (modalManager.isOpen && interactable) {
    const currentValues = {} as CurrentValues;

    if (threeDModel) {
      currentValues.id = threeDModel.id.toString();
      currentValues.fileName = threeDModel.file_name;
      currentValues.textures = threeDModel.image_models.map((im) => {
        const texture = threeDModel.textures.find(t => t.id === im.image_id);

        return {
          id: texture.id.toString(),
          imageUrl: texture.url,
          textureType: im.media_meta.texture_type,
          imageModelId: im.id.toString(),
        };
      });
    }

    return (
      <ThreeDModelUploadModal
        currentValues={currentValues}
        modalManager={modalManager}
        slideObjectId={slideObject.id}
      />
    );
  }

  if (!threeDModel) return null;

  // AIDEV-NOTE: Transform API data to viewer format - prevents missing textures after slide copy
  const viewerData = {
    cameraPos: threeDModel.camera_pos,
    cameraRot: threeDModel.camera_rot,
    cameraTarget: threeDModel.camera_target,
    cameraZoom: threeDModel.camera_zoom,
    id: threeDModel.id.toString(),
    modelUrl: threeDModel.model_url,
    textures: threeDModel.image_models
      .map((im) => {
        const texture = threeDModel.textures.find(t => t.id === im.image_id);
        if (!texture) return null;

        return {
          id: texture.id.toString(),
          imageUrl: texture.url || texture.imageUrl,
          imageModels: [{
            textureType: im.media_meta.texture_type,
          }],
        };
      })
      .filter(Boolean),
  };

  // AIDEV-NOTE: key forces Three.js scene remount when saved camera defaults change (e.g. thumbnail refresh)
  const cameraKey = JSON.stringify([
    viewerData.cameraPos,
    viewerData.cameraRot,
    viewerData.cameraTarget,
    viewerData.cameraZoom,
  ]);

  return <MemoizedThreeDModelViewer key={cameraKey} threeDModel={viewerData} onSuccess={onSuccess} />;
};

export default SlideObject3DModel;
